import type { FurniturePart, Vec2 } from "../../types";
import {
  circle,
  clamp,
  divide,
  ellipse,
  part,
  rect,
  resetPartIds,
  seam,
} from "../geometry";
import { num, str, bool, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

/*
 * Seating templates. Frame reminder: origin at footprint center, +y is the
 * FRONT (where you sit down from), so backrests hug the -y edge and arms run
 * front-to-back along the ±x edges.
 *
 * Every dimension that describes a physical detail (arm thickness, back
 * depth, cushion gap) is a fixed real-world size with clamps rather than a
 * percentage — a 12' sectional and a 5' loveseat both have ~7" arms.
 */

const CUSHION_GAP = 1;
const SEAT_Z = 18;

function backDepth(d: number) {
  return clamp(d * 0.24, 6.5, 11);
}
function armWidth(w: number) {
  return clamp(w * 0.09, 5, 9);
}

/** Shared upholstered seat builder used by sofa / armchair / chaise. */
function buildUpholstered(
  w: number,
  d: number,
  h: number,
  seats: number,
  arms: "both" | "left" | "right" | "none",
  backStyle: "cushions" | "tight"
): FurniturePart[] {
  const parts: FurniturePart[] = [];
  const BACK = backDepth(d);
  const ARM = armWidth(w);
  const armL = arms === "both" || arms === "left" ? ARM : 0;
  const armR = arms === "both" || arms === "right" ? ARM : 0;
  const seatH = Math.min(SEAT_Z, h * 0.55);

  // Frame / base. Slight corner radius so the silhouette reads soft.
  parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, seatH, { cornerRadius: 2 }));

  // Backrest between the arms.
  parts.push(
    part("backrest", rect(-w / 2 + armL, -d / 2, w - armL - armR, BACK), seatH, h, {
      cornerRadius: 1.5,
    })
  );

  // Arms run the full depth.
  if (armL) {
    parts.push(
      part("arm", rect(-w / 2, -d / 2, ARM, d), seatH, h * 0.78, { cornerRadius: 2 })
    );
  }
  if (armR) {
    parts.push(
      part("arm", rect(w / 2 - ARM, -d / 2, ARM, d), seatH, h * 0.78, { cornerRadius: 2 })
    );
  }

  // Seat cushions between arms, in front of the back.
  const innerX = -w / 2 + armL + 0.75;
  const innerW = w - armL - armR - 1.5;
  const seatY = -d / 2 + BACK + 0.75;
  const seatD = d - BACK - 2;
  const cells = divide(innerX, innerW, seats, CUSHION_GAP);
  for (const c of cells) {
    parts.push(
      part("seat", rect(c.start, seatY, c.size, seatD), seatH, seatH + 4, {
        cornerRadius: clamp(Math.min(c.size, seatD) * 0.08, 1, 2.5),
      })
    );
  }

  // Back cushions sit on top of the backrest, one per seat.
  if (backStyle === "cushions") {
    const bcY = -d / 2 + 1;
    const bcD = BACK - 1.75;
    for (const c of cells) {
      parts.push(
        part("cushion", rect(c.start + 0.5, bcY, c.size - 1, bcD), h * 0.6, h, {
          cornerRadius: clamp(bcD * 0.35, 1, 3),
        })
      );
    }
  }
  return parts;
}

export const sofaTemplate: FurnitureTemplate = {
  id: "sofa",
  label: "Sofa",
  frontKind: "seat",
  defaultMaterial: "fabric",
  params: [
    { id: "seats", label: "Seats", type: "int", min: 1, max: 5, default: 3 },
    {
      id: "arms",
      label: "Arms",
      type: "enum",
      default: "both",
      options: [
        { value: "both", label: "Both" },
        { value: "left", label: "Left" },
        { value: "right", label: "Right" },
        { value: "none", label: "None" },
      ],
    },
    {
      id: "back",
      label: "Back",
      type: "enum",
      default: "cushions",
      options: [
        { value: "cushions", label: "Cushions" },
        { value: "tight", label: "Tight" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const seats = num(params, "seats", 3);
    const arms = str(params, "arms", "both") as "both" | "left" | "right" | "none";
    const back = str(params, "back", "cushions") as "cushions" | "tight";
    return { parts: buildUpholstered(w, d, h, seats, arms, back) };
  },
};

export const armchairTemplate: FurnitureTemplate = {
  id: "armchair",
  label: "Armchair",
  frontKind: "seat",
  defaultMaterial: "fabric",
  params: [
    {
      id: "back",
      label: "Back",
      type: "enum",
      default: "tight",
      options: [
        { value: "cushions", label: "Cushion" },
        { value: "tight", label: "Tight" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const back = str(params, "back", "tight") as "cushions" | "tight";
    return { parts: buildUpholstered(w, d, h, 1, "both", back) };
  },
};

/**
 * Sectional: a main run along the back (-y) edge plus one or two returns
 * that extend toward the front along the ±x edges. The footprint is the
 * L / U polygon, not the bounding box.
 */
export const sectionalTemplate: FurnitureTemplate = {
  id: "sectional",
  label: "Sectional",
  frontKind: "seat",
  defaultMaterial: "fabric",
  params: [
    {
      id: "layout",
      label: "Layout",
      type: "enum",
      default: "l-right",
      options: [
        { value: "l-left", label: "L (left)" },
        { value: "l-right", label: "L (right)" },
        { value: "u", label: "U" },
      ],
    },
    { id: "seats", label: "Seats (main run)", type: "int", min: 1, max: 6, default: 3 },
    {
      id: "returnStyle",
      label: "Return",
      type: "enum",
      default: "chaise",
      options: [
        { value: "chaise", label: "Chaise (open)" },
        { value: "sofa", label: "Sofa (with back)" },
      ],
    },
    { id: "seatDepth", label: "Seat depth", type: "int", min: 28, max: 44, default: 36 },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const layout = str(params, "layout", "l-right") as "l-left" | "l-right" | "u";
    const seats = num(params, "seats", 3);
    const returnStyle = str(params, "returnStyle", "chaise") as "chaise" | "sofa";
    // Main run depth (front-to-back of the straight part).
    const SD = clamp(num(params, "seatDepth", 36), 24, Math.max(24, d - 12));
    // Return leg width (its x extent).
    const RW = clamp(Math.min(w * 0.36, SD + 2), 26, Math.max(26, w - 30));
    const BACK = backDepth(SD);
    const ARM = armWidth(w);
    const seatH = Math.min(SEAT_Z, h * 0.55);
    const left = layout === "l-left" || layout === "u";
    const right = layout === "l-right" || layout === "u";
    const rwL = left ? RW : 0;
    const rwR = right ? RW : 0;

    const x0 = -w / 2;
    const x1 = w / 2;
    const y0 = -d / 2;
    const y1 = d / 2;
    const yMain = y0 + SD; // front edge of the main run

    // Footprint polygon (L or U), clockwise starting top-left.
    let footprint: Vec2[];
    if (left && right) {
      footprint = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x1 - rwR, y: y1 },
        { x: x1 - rwR, y: yMain },
        { x: x0 + rwL, y: yMain },
        { x: x0 + rwL, y: y1 },
        { x: x0, y: y1 },
      ];
    } else if (right) {
      footprint = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x1 - rwR, y: y1 },
        { x: x1 - rwR, y: yMain },
        { x: x0, y: yMain },
      ];
    } else {
      footprint = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: yMain },
        { x: x0 + rwL, y: yMain },
        { x: x0 + rwL, y: y1 },
        { x: x0, y: y1 },
      ];
    }

    const parts: FurniturePart[] = [];
    parts.push(part("body", footprint, 0, seatH));

    // Backrest along the whole back edge.
    parts.push(part("backrest", rect(x0, y0, w, BACK), seatH, h, { cornerRadius: 1.5 }));

    // Arms at the open ends of the main run (where there's no return).
    if (!left) parts.push(part("arm", rect(x0, y0, ARM, SD), seatH, h * 0.78, { cornerRadius: 2 }));
    if (!right) parts.push(part("arm", rect(x1 - ARM, y0, ARM, SD), seatH, h * 0.78, { cornerRadius: 2 }));

    // Returns: outer-side backrest + front arm when "sofa" style; open when "chaise".
    function buildReturn(side: "left" | "right") {
      const rx0 = side === "left" ? x0 : x1 - RW;
      const rx1 = rx0 + RW;
      if (returnStyle === "sofa") {
        // Backrest along the outer edge, from the main back down to the front.
        const bx = side === "left" ? rx0 : rx1 - BACK;
        parts.push(part("backrest", rect(bx, y0 + BACK, BACK, d - BACK), seatH, h, { cornerRadius: 1.5 }));
        // Arm at the front end.
        parts.push(part("arm", rect(rx0, y1 - ARM, RW, ARM), seatH, h * 0.78, { cornerRadius: 2 }));
      }
      // Corner cushion (where the main run meets the return).
      const cornerInsetX = side === "left" ? (returnStyle === "sofa" ? BACK : 0.75) : 0.75;
      const cornerInsetR = side === "right" ? (returnStyle === "sofa" ? BACK : 0.75) : 0.75;
      parts.push(
        part(
          "seat",
          rect(rx0 + cornerInsetX, y0 + BACK + 0.75, RW - cornerInsetX - cornerInsetR, SD - BACK - 1.5),
          seatH,
          seatH + 4,
          { cornerRadius: 1.5 }
        )
      );
      // Return cushions along the leg.
      const legStart = yMain + 0.75;
      const legEnd = y1 - (returnStyle === "sofa" ? ARM + 0.75 : 1.5);
      const legLen = legEnd - legStart;
      if (legLen > 6) {
        const n = Math.max(1, Math.round(legLen / 28));
        const cells = divide(legStart, legLen, n, CUSHION_GAP);
        for (const c of cells) {
          parts.push(
            part(
              "chaise",
              rect(rx0 + cornerInsetX, c.start, RW - cornerInsetX - cornerInsetR, c.size),
              seatH,
              seatH + 4,
              { cornerRadius: 1.5 }
            )
          );
        }
        if (returnStyle === "sofa") {
          // Back cushions on the return's backrest.
          const bx = side === "left" ? rx0 + 0.75 : rx1 - BACK + 0.75;
          for (const c of cells) {
            parts.push(
              part("cushion", rect(bx, c.start + 0.5, BACK - 1.75, c.size - 1), h * 0.6, h, {
                cornerRadius: clamp((BACK - 1.75) * 0.35, 1, 3),
              })
            );
          }
        }
      }
    }
    if (left) buildReturn("left");
    if (right) buildReturn("right");

    // Main run seat + back cushions between the arm/returns.
    const mainX0 = x0 + (left ? RW : ARM) + 0.75;
    const mainX1 = x1 - (right ? RW : ARM) - 0.75;
    const mainW = mainX1 - mainX0;
    if (mainW > 4) {
      const cells = divide(mainX0, mainW, seats, CUSHION_GAP);
      for (const c of cells) {
        parts.push(
          part("seat", rect(c.start, y0 + BACK + 0.75, c.size, SD - BACK - 1.5), seatH, seatH + 4, {
            cornerRadius: 1.5,
          })
        );
        parts.push(
          part("cushion", rect(c.start + 0.5, y0 + 1, c.size - 1, BACK - 1.75), h * 0.6, h, {
            cornerRadius: clamp((BACK - 1.75) * 0.35, 1, 3),
          })
        );
      }
    }

    return { parts, footprint };
  },
};

export const chairTemplate: FurnitureTemplate = {
  id: "chair",
  label: "Chair",
  frontKind: "seat",
  defaultMaterial: "wood-mid",
  params: [
    { id: "arms", label: "Arms", type: "bool", default: false },
    {
      id: "seatShape",
      label: "Seat",
      type: "enum",
      default: "square",
      options: [
        { value: "square", label: "Square" },
        { value: "round", label: "Round" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const arms = bool(params, "arms", false);
    const seatShape = str(params, "seatShape", "square");
    const BACK = clamp(d * 0.13, 1.5, 3);
    const ARM = arms ? clamp(w * 0.12, 1.5, 3) : 0;
    const seatH = Math.min(18, h * 0.5);
    const parts: FurniturePart[] = [];
    const seatY = -d / 2 + BACK + 0.5;
    const seatD = d - BACK - 0.5;
    if (seatShape === "round") {
      parts.push(
        part("seat", ellipse(0, seatY + seatD / 2, (w - 2 * ARM) / 2 - 0.25, seatD / 2), 0, seatH)
      );
    } else {
      parts.push(
        part("seat", rect(-w / 2 + ARM, seatY, w - 2 * ARM, seatD), 0, seatH, {
          cornerRadius: clamp(Math.min(w, d) * 0.12, 1, 3),
        })
      );
    }
    parts.push(
      part("backrest", rect(-w / 2 + 0.75, -d / 2, w - 1.5, BACK), seatH, h, { cornerRadius: 1 })
    );
    if (arms) {
      parts.push(part("arm", rect(-w / 2, -d / 2 + BACK, ARM, d - BACK - 2), seatH, h * 0.7, { cornerRadius: 1 }));
      parts.push(part("arm", rect(w / 2 - ARM, -d / 2 + BACK, ARM, d - BACK - 2), seatH, h * 0.7, { cornerRadius: 1 }));
    }
    return { parts };
  },
};

export const stoolTemplate: FurnitureTemplate = {
  id: "stool",
  label: "Stool",
  frontKind: "none",
  defaultMaterial: "wood-dark",
  params: [
    {
      id: "shape",
      label: "Seat",
      type: "enum",
      default: "round",
      options: [
        { value: "round", label: "Round" },
        { value: "square", label: "Square" },
      ],
    },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const shape = str(params, "shape", "round");
    const r = Math.min(w, d) / 2;
    const parts: FurniturePart[] = [];
    if (shape === "round") {
      parts.push(part("seat", ellipse(0, 0, w / 2, d / 2), 0, h));
      parts.push(part("cushion", circle(0, 0, r * 0.7), h, h + 1.5));
      return { parts, footprint: ellipse(0, 0, w / 2, d / 2) };
    }
    parts.push(part("seat", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: clamp(r * 0.2, 1, 3) }));
    parts.push(
      part("cushion", rect(-w / 2 + r * 0.3, -d / 2 + r * 0.3, w - r * 0.6, d - r * 0.6), h, h + 1.5, {
        cornerRadius: clamp(r * 0.15, 0.75, 2),
      })
    );
    return { parts };
  },
};

export const benchTemplate: FurnitureTemplate = {
  id: "bench",
  label: "Bench",
  frontKind: "none",
  defaultMaterial: "wood-mid",
  params: [{ id: "upholstered", label: "Upholstered top", type: "bool", default: false }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const upholstered = bool(params, "upholstered", false);
    const parts: FurniturePart[] = [];
    parts.push(part("seat", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: clamp(d * 0.1, 0.5, 2) }));
    if (upholstered) {
      parts.push(
        part("cushion", rect(-w / 2 + 1, -d / 2 + 1, w - 2, d - 2), h, h + 2, {
          cornerRadius: clamp(d * 0.12, 1, 2.5),
        })
      );
    } else {
      // Plank seams every ~5" across the width.
      const n = Math.max(1, Math.round(d / 5));
      const seams = [];
      for (let i = 1; i < n; i++) {
        const y = -d / 2 + (d / n) * i;
        seams.push(seam(-w / 2 + 0.75, y, w / 2 - 0.75, y));
      }
      parts[0].seams = seams;
    }
    return { parts };
  },
};

export const ottomanTemplate: FurnitureTemplate = {
  id: "ottoman",
  label: "Ottoman",
  frontKind: "none",
  defaultMaterial: "leather",
  params: [
    {
      id: "shape",
      label: "Shape",
      type: "enum",
      default: "rect",
      options: [
        { value: "rect", label: "Rectangle" },
        { value: "round", label: "Round" },
      ],
    },
    { id: "tufted", label: "Tufted", type: "bool", default: false },
  ],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const shape = str(params, "shape", "rect");
    const tufted = bool(params, "tufted", false);
    const parts: FurniturePart[] = [];
    if (shape === "round") {
      const fp = ellipse(0, 0, w / 2, d / 2);
      parts.push(part("body", fp, 0, h));
      parts.push(part("cushion", ellipse(0, 0, w / 2 - 1.5, d / 2 - 1.5), h, h + 2));
      if (tufted) {
        parts[1].seams = [seam(-w * 0.3, 0, w * 0.3, 0), seam(0, -d * 0.3, 0, d * 0.3)];
      }
      return { parts, footprint: fp };
    }
    const r = clamp(Math.min(w, d) * 0.15, 1.5, 4);
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: r }));
    parts.push(
      part("cushion", rect(-w / 2 + 1.25, -d / 2 + 1.25, w - 2.5, d - 2.5), h, h + 2, {
        cornerRadius: Math.max(0.75, r - 1),
      })
    );
    if (tufted) {
      const seams = [];
      const cols = Math.max(1, Math.round(w / 12));
      const rows = Math.max(1, Math.round(d / 12));
      for (let i = 1; i < cols; i++) {
        const x = -w / 2 + (w / cols) * i;
        seams.push(seam(x, -d / 2 + 2, x, d / 2 - 2));
      }
      for (let j = 1; j < rows; j++) {
        const y = -d / 2 + (d / rows) * j;
        seams.push(seam(-w / 2 + 2, y, w / 2 - 2, y));
      }
      parts[1].seams = seams;
    }
    return { parts };
  },
};
